import { Link } from '@tanstack/react-router';
import { ChevronRight, TriangleAlert } from 'lucide-react';

import { useGapExecution } from '@/shared/hooks/useGapReview';
import { useStickyDisclosure } from '../hooks/useStickyDisclosure';
import { pluralize, shortDate } from '../lib/format';
import { totalsOf, verdictTone } from '../lib/run-audit';

/**
 * The selected run's report, in the queue.
 *
 * This is the material the orchestrator used to publish as a Notion page per
 * run. There is no page link to follow: the run document REPLACED that page
 * (see `GapAnalysisScope`), so the summary is assembled from the execution
 * itself — what the loop did, what it adjudicated, and what it could not
 * reach. It sits under the picker because that is where the question comes up:
 * before working through twenty gaps, a reviewer wants to know what produced
 * them and whether anything about the run was broken.
 *
 * Deliberately a summary and not the audit. Everything here is one number or
 * one sentence; the full record — the candidate funnel, the loop's iterations,
 * per-scope coverage — is a click away in the run audit.
 *
 * It is also above the gap list, which is what the queue is for, so it collapses
 * to its header — and that choice is sticky (`useStickyDisclosure`), because a
 * reviewer who wants the room wants it for the whole run, not for one draft.
 * Collapsed, the header still carries the two numbers that decide whether to
 * open it: how many gaps the run produced, and whether anything went wrong.
 *
 * Collapsed is the default: the run is read once, at the start, while the queue
 * below it is read on every draft. Whoever wants the summary opens it and keeps
 * it open — the reverse costs everyone else height on all twenty gaps.
 */
export function RunSummaryCard({ executionId }: { executionId: string }) {
  const { data: execution, isPending, error } = useGapExecution(executionId);
  const { isOpen, setOpen } = useStickyDisclosure('queue:run-summary', false);

  if (isPending) {
    return <div className="gr-runsum gr-runsum-quiet">Loading the run…</div>;
  }

  if (error !== null) {
    return (
      <div className="gr-runsum gr-runsum-quiet">
        Run summary unavailable — {error.message}
      </div>
    );
  }

  const totals = totalsOf(execution);
  const { coverage, recurringPatterns } = execution.overview;

  const pendingGapCount = execution.scopes.reduce(
    (runningTotal, scope) =>
      runningTotal + scope.coverage.pendingValidationGapIds.length,
    0
  );
  const failedScopeCount = Math.max(
    coverage.scopesFailed,
    execution.failedScopes.length
  );
  const hasWarning = pendingGapCount > 0 || failedScopeCount > 0;

  return (
    <section className="gr-runsum" data-open={isOpen}>
      <div className="gr-runsum-head">
        <button
          type="button"
          className="gr-runsum-toggle"
          aria-expanded={isOpen}
          onClick={() => setOpen(!isOpen)}
        >
          <ChevronRight className="gr-runsum-caret" aria-hidden />
          <span className="gr-runsum-label">Run</span>
          {/* Open, the same numbers sit one line below in full; repeating them
              in the header would only make the block taller again. */}
          {!isOpen && (
            <span className="gr-runsum-digest">
              {pluralize(totals.gapCount, 'gap')} ·{' '}
              {pluralize(totals.scopeCount, 'scope')}
            </span>
          )}
          {!isOpen && hasWarning && (
            <TriangleAlert className="gr-runsum-warnmark" aria-hidden />
          )}
        </button>
        <Link
          className="gr-linkbtn"
          to="/gap-review/run/$executionId"
          params={{ executionId }}
        >
          Audit →
        </Link>
      </div>

      {isOpen && (
        <div className="gr-runsum-body">
          {/* Counts and verdicts share one wrapping row: four pills and four
              numbers are the same kind of fact, and stacked they cost the queue
              two lines for one answer. */}
          <div className="gr-runsum-stats">
            <span className="gr-runsum-counts">
              {pluralize(totals.gapCount, 'gap')} ·{' '}
              {pluralize(totals.candidateCount, 'candidate')} ·{' '}
              {pluralize(totals.scopeCount, 'scope')} ·{' '}
              {pluralize(execution.runsExecuted, 'run')}
            </span>
            {totals.verdictCounts.map(({ verdict, count }) => (
              <span
                className="gr-vpill"
                key={verdict}
                data-tone={verdictTone(verdict)}
              >
                {count} {verdict.toLowerCase()}
              </span>
            ))}
          </div>

          {/* The two dates a verdict depends on. Silence after the cutoff is a
              sync artifact rather than customer inactivity, which is exactly the
              mistake a reviewer makes when they cannot see the cutoff. */}
          <p className="gr-runsum-meta">
            stopped {execution.stopReason} · cutoff{' '}
            {shortDate(execution.dataCutoff)} · {execution.variant}
            {/* Absent on executions that predate the field; nothing backfilled
                them. */}
            {!!execution.models?.length && ` · ${execution.models.join(', ')}`}
          </p>

          {hasWarning && (
            <div className="gr-runsum-warn">
              <TriangleAlert />
              <span>
                {[
                  pendingGapCount > 0
                    ? `${pluralize(pendingGapCount, 'gap')} never adjudicated`
                    : '',
                  failedScopeCount > 0
                    ? `${pluralize(failedScopeCount, 'scope')} failed`
                    : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
          )}

          {recurringPatterns.length > 0 && (
            <ul className="gr-runsum-patterns">
              {recurringPatterns.map((pattern) => (
                <li key={pattern.pattern.slice(0, 48)}>
                  {pattern.pattern}{' '}
                  <b>{pluralize(pattern.affectedGapIds.length, 'gap')}</b>
                </li>
              ))}
            </ul>
          )}

          <details className="gr-fold gr-runsum-fold">
            <summary>What the run covered</summary>
            <div className="gr-fold-body">
              {coverage.detectorsNote}
              {'\n\n'}
              {execution.consistencyPass.result}
            </div>
          </details>
        </div>
      )}
    </section>
  );
}
